import { HomeIcon } from "@heroicons/react/24/outline";
import * as Sentry from "@sentry/react";
import { useEffect, type PropsWithChildren } from "react";
import { Button } from "@/core/components/ui/button";
import { useEnv } from "@/core/env";

const ErrorFallback = ({
  error,
  resetError,
}: {
  error: unknown;
  resetError: () => void;
}) => {
  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    try {
      // Clear caches using the Cache API
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((name) => caches.delete(name)));

      // Clear service worker if present
      if ("serviceWorker" in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((reg) => reg.unregister()));
      }

      resetError();
      // Force reload the page
      window.location.href = "/";
    } catch (err) {
      console.error(err);
      // Fallback to simple reload if cache clearing fails
      window.location.href = "/";
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center p-4 text-center">
      <h1 className="mb-2 text-4xl font-bold">Oops!</h1>
      <p className="mb-4 text-lg text-muted-foreground">
        Looks like there was an error with our system!
      </p>
      {import.meta.env.DEV && !!error && (
        <pre className="mb-8 max-w-2xl overflow-auto rounded-lg bg-muted p-4 text-left text-sm">
          {error instanceof Error ? error.message : String(error)}
        </pre>
      )}
      <Button asChild variant="text">
        <a
          href="/"
          onClick={handleClick}
          className="flex flex-row items-center gap-2"
        >
          <HomeIcon className="h-4 w-4" />
          Back to Home
        </a>
      </Button>
    </div>
  );
};

export const SentryProvider = ({ children }: PropsWithChildren) => {
  const { sentryDsn, appEnv } = useEnv();

  useEffect(() => {
    Sentry.init({
      dsn: sentryDsn,
      integrations: [
        Sentry.browserTracingIntegration(),
        // Sentry.replayIntegration(),
      ],
      // Performance Monitoring
      tracesSampleRate: 1.0,
      tracePropagationTargets: ["localhost", /^https?:\/\/.*\.govolca\.com/],
      // Session Replay
      // replaysSessionSampleRate: 0.1,
      // replaysOnErrorSampleRate: 1.0,
      // Set environment
      environment: appEnv || "development",
    });
  }, [sentryDsn, appEnv]);

  return (
    <Sentry.ErrorBoundary fallback={ErrorFallback}>
      {children}
    </Sentry.ErrorBoundary>
  );
};

export default SentryProvider;
